// A forecast row's own routes — its creation, schedule and notify writes, the result read and
// the resolver — as raw `fetch` pass-throughs (C-33: network I/O lives in `services/` or
// `lib/apiClient`). Each function is the `fetch` its call site made, moved here unchanged and
// returning the `Response` untouched, so the caller keeps its own status handling (a `404`
// on the result read means "still running", C-08). `forecastApi.ts` holds the typed,
// never-throws calls over `lib/apiClient`; these are the sites not yet on it.
import { intentHeaders as rawIntentHeaders } from "../lib/telemetry/intentEmitter.js";

/** `x-intent-id` for `intentId`, or no header (the emitter is JS; typed here for `fetch`). */
const intentHeaders = (intentId: string | null): Record<string, string> =>
  rawIntentHeaders(intentId) as Record<string, string>;

const JSON_HEADERS = { "Content-Type": "application/json" };

/** The user intent a request belongs to, sent as `x-intent-id`. */
export interface IntentOptions {
  intentId?: string | null;
}

/** `POST /api/forecasts`: create a row from a wire object (the tour's example forecast). */
export function postForecastRow(wire: unknown): Promise<Response> {
  return fetch("/api/forecasts", {
    method: "POST",
    credentials: "include",
    headers: JSON_HEADERS,
    body: JSON.stringify(wire),
  });
}

/** `PATCH /api/forecasts/:id`: the owner's row, by its wire client id. */
export function patchForecast(
  id: string,
  body: Record<string, unknown>,
  { intentId = null }: IntentOptions = {},
): Promise<Response> {
  return fetch(`/api/forecasts/${encodeURIComponent(id)}`, {
    method: "PATCH",
    credentials: "include",
    headers: { ...JSON_HEADERS, ...intentHeaders(intentId) },
    body: JSON.stringify(body),
  });
}

/** `PUT /api/forecasts/:forecastId/notify`: a shared row's viewer prefs, by its pg UUID. */
export function putForecastNotify(
  forecastId: string,
  body: Record<string, unknown>,
  { intentId = null }: IntentOptions = {},
): Promise<Response> {
  return fetch(`/api/forecasts/${encodeURIComponent(forecastId)}/notify`, {
    method: "PUT",
    credentials: "include",
    headers: { ...JSON_HEADERS, ...intentHeaders(intentId) },
    body: JSON.stringify(body),
  });
}

/** `GET /api/forecasts/:traceId/result`, abortable; a `404` means the run is still going. */
export function fetchForecastResult(
  traceId: string,
  { signal }: { signal?: AbortSignal } = {},
): Promise<Response> {
  return fetch(`/api/forecasts/${encodeURIComponent(traceId)}/result`, {
    credentials: "include",
    signal,
  });
}

/** `POST /api/forecasts/:id/resolve`: submit a resolution request, abortable. */
export function postResolve(
  id: string,
  payload: Record<string, unknown>,
  { signal }: { signal?: AbortSignal } = {},
): Promise<Response> {
  return fetch(`/api/forecasts/${encodeURIComponent(id)}/resolve`, {
    method: "POST",
    headers: JSON_HEADERS,
    body: JSON.stringify(payload),
    signal,
    credentials: "include",
  });
}

/**
 * `GET /api/forecasts/:id/resolve/:traceId[?forecastId=]`: poll the verdict (a `404` whose body
 * says `in_flight` means keep polling), abortable.
 */
export function fetchResolveTrace(
  id: string,
  traceId: string,
  forecastUuid: string | null | undefined,
  { signal }: { signal?: AbortSignal } = {},
): Promise<Response> {
  const q = forecastUuid ? `?forecastId=${encodeURIComponent(forecastUuid)}` : "";
  return fetch(
    `/api/forecasts/${encodeURIComponent(id)}/resolve/${encodeURIComponent(traceId)}${q}`,
    { signal, credentials: "include" },
  );
}
